// Library dialog state lives in the URL (?dialog=...&section=...&step=...)
// so it survives reloads and is deep-linkable. This hook is the one knob any
// library page or descendant should reach for when they need to read or
// change the active dialog.

import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useRouteQueryState } from '../../../hooks/useRouteQueryState';
import {
  type LibraryDialog,
  type LibraryRouteEnumState,
  type LibraryRouteFacetState,
  LIBRARY_QUERY_CONFIG,
} from '../libraryRouteTypes';

interface UseLibraryDialogResult {
  dialog: LibraryDialog | null;
  openDialog: (dialog: LibraryDialog) => void;
  setDialog: (dialog: LibraryDialog | null) => void;
}

export function useLibraryDialog(): UseLibraryDialogResult {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { values, updateEnums } = useRouteQueryState<
    LibraryRouteFacetState,
    LibraryRouteEnumState
  >({
    pathname: location.pathname,
    searchParams,
    navigate,
    ...LIBRARY_QUERY_CONFIG,
  });
  const dialog = values.dialog;

  const openDialog = useCallback(
    (nextDialog: LibraryDialog) => {
      const nextSection =
        nextDialog === 'blocked-users'
          ? 'player-access'
          : nextDialog === 'chat-commands'
            ? 'poppy-chat'
            : nextDialog === 'custom-words'
              ? 'censorship'
              : null;

      updateEnums(
        {
          dialog: nextDialog,
          section: nextSection,
          loginStep: 'choice',
        },
        { replace: true }
      );
    },
    [updateEnums]
  );

  const setDialog = useCallback(
    (nextDialog: LibraryDialog | null) => {
      const updates: Partial<LibraryRouteEnumState> = {
        dialog: nextDialog,
      };

      if (nextDialog !== 'login') {
        updates.loginStep = 'choice';
      }

      updateEnums(updates, { replace: true });
    },
    [updateEnums]
  );

  return { dialog, openDialog, setDialog };
}
